/**
 * FeaturedSellersCarousel - Verified Boutiques Trust Row
 * Shows only verified sellers (min 3 to display), compact horizontal layout
 */

import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, CheckCircle, Store, Star } from 'lucide-react';
import { 
  LadiesHubColors, 
  LadiesHubRadii, 
  LadiesHubSpacing, 
  LadiesHubMotion,
  LadiesHubFeatures 
} from '../hubs/ladies/LadiesHubPremiumConfig';

const colors = LadiesHubColors;
const radii = LadiesHubRadii;
const spacing = LadiesHubSpacing;
const motionConfig = LadiesHubMotion;

// Get initials from store name
function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

// Single seller card
function SellerCard({ seller, onShopClick, lang, isRTL }) {
  const storeName = seller.store_name || seller.name || seller.username || 'Store';
  const logoUrl = seller.logo || seller.avatar;
  const rating = seller.rating || seller.seller_rating;
  
  return (
    <motion.div
      whileTap={{ scale: 0.97 }}
      onClick={() => onShopClick(seller)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 14,
        background: colors.surface,
        border: '1px solid rgba(16,185,129,0.2)',
        cursor: 'pointer',
        minWidth: 'fit-content',
        maxWidth: 200,
        flexShrink: 0,
      }}
      data-testid={`featured-seller-${seller.user_id || seller._id}`}
    >
      {/* Logo or Initial */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: logoUrl ? 'transparent' : 'linear-gradient(135deg, #10B981 0%, #1F6F3A 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
          border: '2px solid rgba(16,185,129,0.3)',
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={storeName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span
          style={{
            display: logoUrl ? 'none' : 'flex',
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {getInitials(storeName)}
        </span>
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginBottom: 2,
          }}
        >
          <CheckCircle size={12} color="#10B981" />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: colors.ink,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {storeName}
          </span>
        </div>

        {/* Rating (optional) */}
        {rating > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 11,
              color: colors.muted,
            }}
          >
            <Star size={10} fill="#F59E0B" color="#F59E0B" />
            <span>{rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Shop CTA */}
      <button
        style={{
          padding: '6px 10px',
          borderRadius: 8,
          background: 'rgba(16,185,129,0.12)',
          border: 'none',
          color: '#1F6F3A',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onShopClick(seller);
        }}
      >
        {lang === 'ar' ? 'تسوّق' : 'Shop'}
      </button>
    </motion.div>
  );
}

export default function FeaturedSellersCarousel({
  sellers = [],
  onShopClick,
  lang = 'en',
  isRTL = false,
}) {
  const scrollRef = useRef(null);

  // Filter to only verified sellers
  const verifiedSellers = sellers.filter(
    (s) => s.isVerifiedSeller === true || s.verification?.status === 'verified_boutique'
  );

  // Hide if less than configured minimum verified sellers
  if (verifiedSellers.length < LadiesHubFeatures.minVerifiedSellersForCarousel) {
    return null;
  }

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 180;
    const actualScroll = isRTL ? -direction * scrollAmount : direction * scrollAmount;
    scrollRef.current.scrollBy({ left: actualScroll, behavior: 'smooth' });
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={motionConfig.fadeSlide}
      className="featured-sellers-section"
      style={{
        padding: '12px 10px',
        background: 'rgba(16,185,129,0.04)',
        borderBottom: '1px solid rgba(16,185,129,0.12)',
      }}
      data-testid="featured-sellers-carousel"
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
          paddingInline: 4,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Store size={14} color="#10B981" />
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: colors.ink,
            }}
          >
            {lang === 'ar' ? 'متاجر موثوقة' : 'Verified Boutiques'}
          </span>
          <span
            style={{
              padding: '2px 6px',
              borderRadius: 6,
              background: 'rgba(16,185,129,0.15)',
              fontSize: 10,
              fontWeight: 600,
              color: '#1F6F3A',
            }}
          >
            {verifiedSellers.length}
          </span>
        </div>

        {/* Scroll arrows */}
        <div style={{ display: 'flex', gap: 4 }}>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll(-1)}
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: colors.surface,
              border: '1px solid rgba(16,185,129,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#10B981',
            }}
            aria-label="Previous"
          >
            {isRTL ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll(1)}
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: colors.surface,
              border: '1px solid rgba(16,185,129,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#10B981',
            }}
            aria-label="Next"
          >
            {isRTL ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
          </motion.button>
        </div>
      </div>

      {/* Sellers Row */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: 10,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: 2,
        }}
        className="hide-scrollbar"
      >
        {verifiedSellers.map((seller) => (
          <SellerCard
            key={seller.user_id || seller._id || seller.email}
            seller={seller}
            onShopClick={onShopClick}
            lang={lang}
            isRTL={isRTL}
          />
        ))}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        html.dark .featured-sellers-section {
          background: rgba(16,185,129,0.06) !important;
          border-bottom-color: rgba(16,185,129,0.15) !important;
        }
        html.dark [data-testid^="featured-seller-"] {
          background: #292524 !important;
          border-color: rgba(16,185,129,0.25) !important;
        }
        html.dark [data-testid^="featured-seller-"] span {
          color: #FED7AA !important;
        }
      `}</style>
    </motion.section>
  );
}
